import {
  Box,
  chakra,
  FormControl,
  FormLabel,
  HStack,
  keyframes,
  Spinner,
  Stack,
  Switch,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import Header from '../../../../components/Header/Header';
import CardHeaderMobile from '../../../../components/Mobile/header';
import NavigationButtonMobilePage from '../../../../components/Mobile/navigationButton';
import { useLocation, useNavigate } from 'react-router-dom';
import InputMobile from '../../../../components/Input/Mobile';
import { FaIdCard } from 'react-icons/fa';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useRequestDetailSelectiveProcess } from '../../../../services/hooks/SelectiveProcess/useRequestDetailSelectiveProcess';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

interface IOwnProps {
  isLoading: boolean;
  onCpfVerify(data: FieldValues): void;
}

const ValidateCpfMobilePage: React.FC<IOwnProps> = ({ isLoading, onCpfVerify }) => {
  const selectiveProcessId = useLocation().pathname.split('/')[2];
  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const { handleSubmit, watch, register } = useFormContext();
  const navigate = useNavigate();

  const accent = '#222';

  const { data, isLoading: spLoading } = useRequestDetailSelectiveProcess(selectiveProcessId || '');

  const isForeigner = watch('is_foreign');

  return (
    <>
      <Stack height="calc(100% - 45px)" w="100%" top={0} bg="white" gap={0}>
        <Header />
        <VStack mt={0} flex={1} overflowY="auto" py={2} px={3}>
          <AnimatedBox
            bgGradient={bgGradient}
            borderRadius="lg"
            boxShadow="lg"
            p={{ base: 5, md: 10 }}
            borderWidth={'1px'}
            borderColor={borderColor}
            bg="white"
            w="100%"
            flex={1}
            display="flex"
          >
            <VStack
              boxSizing="border-box"
              maxW="lg"
              spacing={4}
              align="stretch"
              as="form"
              id="form-valid-cpf"
              onSubmit={handleSubmit(onCpfVerify)}
              flex={1}
            >
              <CardHeaderMobile
                title="Fórmulario de inscrição"
                subTitle={`Informe o número do seu ${isForeigner ? 'Passaporte' : 'CPF'} no campo abaixo. Se já possuir cadastro, será direcionado automaticamente para a tela de login. Caso contrário, será encaminhado para a tela de cadastro para criar sua conta.`}
                gap={1}
                px={3}
              />
              <VStack spacing={0} align="stretch" h="100%" justify="center" alignItems="center">
                {(spLoading && <Spinner size="xl" emptyColor="gray.200" />) || (
                  <Stack mt="-10%" h="fit-content" w="100%">
                    <Text
                      bgColor="primaryColor"
                      color="white"
                      boxSizing="border-box"
                      py={1}
                      px={5}
                      fontWeight="bold"
                      borderTopRadius="md"
                      fontSize="small"
                      textTransform="uppercase"
                    >
                      {data?.name || '--'}
                    </Text>
                    <VStack
                      boxShadow="lg"
                      px={5}
                      align="center"
                      py="5%"
                      minH="fit-content"
                      bg="white"
                      borderBottomRadius="md"
                    >
                      {data?.allows_foreigners && (
                        <FormControl mb={3}>
                          <HStack alignItems="center">
                            <Switch {...register('is_foreign')} size="md" colorScheme="blue" />
                            <FormLabel htmlFor="expedicao" color="gray.500" fontSize="sm" p={0} m={0}>
                              Sou estrangeiro não naturalizado
                            </FormLabel>
                          </HStack>
                        </FormControl>
                      )}

                      <InputMobile
                        textTransform={'uppercase'}
                        name="document_number"
                        placeholderLabel={isForeigner ? 'Número do Passaporte' : 'CPF'}
                        iconLeft={<FaIdCard size={12} color={accent} />}
                        {...(!isForeigner ? { mask: '999.999.999-99' } : undefined)}
                        onInput={
                          isForeigner
                            ? (e) => {
                                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9A-Za-z]/g, '');
                              }
                            : undefined
                        }
                      />
                      <Text textAlign="left" w="100%" fontSize="xs" color="gray.500">
                        {!isForeigner ? 'Formato: 000.000.000-00' : 'Formato: 00000000000'}
                      </Text>
                    </VStack>
                  </Stack>
                )}
              </VStack>
            </VStack>
          </AnimatedBox>
        </VStack>
      </Stack>
      <NavigationButtonMobilePage
        absolute
        mainButtonText="Continuar"
        goBackButtonText="Voltar"
        onGoBackButton={() => {
          navigate(-1);
        }}
        formId="form-valid-cpf"
        typeButton="submit"
        isLoading={isLoading}
      />
    </>
  );
};

export default ValidateCpfMobilePage;
